import React, { useState } from "react";
import ConfirmationBox from "../../../../componentLibrary/ConfirmationBox";
import GoogleCalendarIcon from "../../../../componentLibrary/icons/GoogleCalendarIcon";
import AppleCalendarIcon from "../../../../componentLibrary/icons/AppleCalendarIcon";
import {
  getEventStartEndDates,
  formatDateForCalendar,
} from "../../utils/addToCalendarDefaults";

export type AddToCalendarBacklinkChoice =
  | "toolbox-and-calendar"
  | "calendar-only";

type CalendarTarget = "google" | "apple";

interface AddToCalendarComponentProps {
  description?: string;
  eventTitle?: string;
  recurrence?: string;
  startTime?: string;
  endTime?: string;
  /** When on lesson page: full URL of the lesson for calendar description backlink */
  lessonPageUrl?: string;
  /** When on lesson page: full URL of the toolbox for calendar description backlink */
  toolboxUrl?: string;
  /** Called when user chooses "Add to toolbox and calendar" (optional; backlink still added) */
  onAddToToolbox?: () => Promise<void>;
  /** Whether to add a backlink (toolbox or lesson) to the calendar description. Default true. */
  addBacklink?: boolean;
  /** When true, page is already in the user's toolbox: skip modal and use toolbox backlink. */
  isPageInToolbox?: boolean;
}

const AddToCalendarComponent: React.FC<AddToCalendarComponentProps> = ({
  description,
  eventTitle,
  recurrence = "NONE",
  startTime = "",
  endTime = "",
  lessonPageUrl,
  toolboxUrl,
  onAddToToolbox,
  addBacklink = true,
  isPageInToolbox = false,
}) => {
  const [showBacklinkModal, setShowBacklinkModal] = useState(false);
  const [backlinkChoice, setBacklinkChoice] =
    useState<AddToCalendarBacklinkChoice>("toolbox-and-calendar");
  const [pendingCalendar, setPendingCalendar] = useState<CalendarTarget | null>(
    null,
  );

  const showBacklinkOptions =
    addBacklink !== false && Boolean(lessonPageUrl) && Boolean(toolboxUrl);
  // When toolbox isn't available (e.g. lesson has no module), we still add a
  // backlink using the lesson URL if addBacklink is true (see handleAddToCalendar).
  const skipModalUseToolboxBacklink = showBacklinkOptions && isPageInToolbox;

  const buildDetailsWithBacklink = (
    backlinkUrl: string,
    label: string,
  ): string => {
    const base = (description || "").trim();
    const line = base ? "\n\n" : "";
    return `${base}${line}${label}: ${backlinkUrl}`;
  };

  /** Escape text for iCalendar (backslash, semicolon, comma, newlines) */
  const escapeIcsText = (text: string) =>
    text
      .replace(/\\/g, "\\\\")
      .replace(/;/g, "\\;")
      .replace(/,/g, "\\,")
      .replace(/\n/g, "\\n");

  const generateIcsContent = (
    backlinkUrl?: string,
    backlinkLabel?: string,
  ): string => {
    const { startDate, endDate } = getEventStartEndDates(startTime, endTime);
    const details =
      backlinkUrl && backlinkLabel
        ? buildDetailsWithBacklink(backlinkUrl, backlinkLabel)
        : description || "";
    const summary = eventTitle || "Reminder";
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}@psych4life`;
    const dtstamp = formatDateForCalendar(new Date());
    const dtstart = formatDateForCalendar(startDate);
    const dtend = formatDateForCalendar(endDate);
    const lines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Psych4Life//Add to Calendar//EN",
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${dtstamp}`,
      `DTSTART:${dtstart}`,
      `DTEND:${dtend}`,
      `SUMMARY:${escapeIcsText(summary)}`,
      `DESCRIPTION:${escapeIcsText(details)}`,
    ];
    if (recurrence && recurrence !== "NONE") {
      lines.push(`RRULE:FREQ=${recurrence}`);
    }
    lines.push("END:VEVENT", "END:VCALENDAR");
    return lines.join("\r\n");
  };

  const triggerIcsDownload = (backlinkUrl?: string, backlinkLabel?: string) => {
    const ics = generateIcsContent(backlinkUrl, backlinkLabel);
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Name the file with the "P4L - event title", removing invalid characters
    const safeName =
      (eventTitle || "Reminder").replace(/[/\\:*?"<>|]/g, "").trim() ||
      "Reminder";
    a.download = `P4L - ${safeName}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generateGoogleCalendarUrl = (
    backlinkUrl?: string,
    backlinkLabel?: string,
  ) => {
    const { startDate, endDate } = getEventStartEndDates(startTime, endTime);
    const startDateStr = formatDateForCalendar(startDate);
    const endDateStr = formatDateForCalendar(endDate);
    const details =
      backlinkUrl && backlinkLabel
        ? buildDetailsWithBacklink(backlinkUrl, backlinkLabel)
        : description || "";

    // Build Google Calendar URL parameters
    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: eventTitle || "Reminder",
      details,
      dates: `${startDateStr}/${endDateStr}`,
    });

    // Add recurrence rule if not "NONE"
    if (recurrence && recurrence !== "NONE") {
      params.append("recur", `RRULE:FREQ=${recurrence}`);
    }

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const openCalendarWithBacklink = (choice: AddToCalendarBacklinkChoice) => {
    const backlinkUrl =
      choice === "toolbox-and-calendar" ? toolboxUrl : lessonPageUrl;
    const backlinkLabel =
      choice === "toolbox-and-calendar" ? "View in My Toolbox" : "View lesson";

    // If we don't have a valid backlink URL, fall back to a calendar entry
    // without backlink information to avoid confusing "undefined" links.
    if (!backlinkUrl) {
      const calendarUrl = generateGoogleCalendarUrl();
      window.open(calendarUrl, "_blank", "noopener,noreferrer");
      return;
    }

    const calendarUrl = generateGoogleCalendarUrl(backlinkUrl, backlinkLabel);
    window.open(calendarUrl, "_blank", "noopener,noreferrer");
  };

  const openAppleCalendarWithBacklink = (
    choice: AddToCalendarBacklinkChoice,
  ) => {
    const backlinkUrl =
      choice === "toolbox-and-calendar" ? toolboxUrl : lessonPageUrl;
    const backlinkLabel =
      choice === "toolbox-and-calendar" ? "View in My Toolbox" : "View lesson";
    if (!backlinkUrl) {
      triggerIcsDownload();
      return;
    }
    triggerIcsDownload(backlinkUrl, backlinkLabel);
  };

  const handleAddToCalendar = () => {
    if (skipModalUseToolboxBacklink) {
      openCalendarWithBacklink("toolbox-and-calendar");
      return;
    }
    if (showBacklinkOptions) {
      setPendingCalendar("google");
      setBacklinkChoice("toolbox-and-calendar");
      setShowBacklinkModal(true);
      return;
    }
    // Fallback: addBacklink is true but toolbox isn't available (e.g. no module).
    // Use lesson URL as backlink so the content-manager backlink setting is still honored.
    if (addBacklink !== false && lessonPageUrl) {
      const calendarUrl = generateGoogleCalendarUrl(
        lessonPageUrl,
        "View lesson",
      );
      window.open(calendarUrl, "_blank", "noopener,noreferrer");
      return;
    }
    // Fallback: addBacklink is true and we have toolbox URL only (e.g. opened from toolbox).
    if (addBacklink !== false && toolboxUrl) {
      const calendarUrl = generateGoogleCalendarUrl(
        toolboxUrl,
        "View in My Toolbox",
      );
      window.open(calendarUrl, "_blank", "noopener,noreferrer");
      return;
    }
    const calendarUrl = generateGoogleCalendarUrl();
    window.open(calendarUrl, "_blank", "noopener,noreferrer");
  };

  const handleAddToAppleCalendar = () => {
    if (skipModalUseToolboxBacklink) {
      openAppleCalendarWithBacklink("toolbox-and-calendar");
      return;
    }
    if (showBacklinkOptions) {
      setPendingCalendar("apple");
      setBacklinkChoice("toolbox-and-calendar");
      setShowBacklinkModal(true);
      return;
    }
    if (addBacklink !== false && lessonPageUrl) {
      triggerIcsDownload(lessonPageUrl, "View lesson");
      return;
    }
    if (addBacklink !== false && toolboxUrl) {
      triggerIcsDownload(toolboxUrl, "View in My Toolbox");
      return;
    }
    triggerIcsDownload();
  };

  const handleConfirmBacklinkModal = async () => {
    if (backlinkChoice === "toolbox-and-calendar" && onAddToToolbox) {
      await onAddToToolbox();
    }
    if (pendingCalendar === "google") {
      openCalendarWithBacklink(backlinkChoice);
    } else if (pendingCalendar === "apple") {
      openAppleCalendarWithBacklink(backlinkChoice);
    }
    setPendingCalendar(null);
    setShowBacklinkModal(false);
  };

  return (
    <>
      <div className="px-4 py-3 flex items-center gap-3">
        <span className="text-base font-medium text-gray-700">
          Add to Calendar:
        </span>
        <button
          onClick={handleAddToCalendar}
          className="flex items-center justify-center hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
          aria-label="Add to Google Calendar"
          title="Add to Google Calendar"
        >
          <GoogleCalendarIcon />
        </button>
        <button
          onClick={handleAddToAppleCalendar}
          className="flex items-center justify-center hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
          aria-label="Add to Apple Calendar"
          title="Add to Apple Calendar"
        >
          <AppleCalendarIcon />
        </button>
      </div>

      <ConfirmationBox
        isOpen={showBacklinkModal}
        title="Add this to your toolbox?"
        message={
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="backlink-choice"
                checked={backlinkChoice === "toolbox-and-calendar"}
                onChange={() => setBacklinkChoice("toolbox-and-calendar")}
                className="mt-1"
              />
              <span>Add to toolbox and calendar</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="backlink-choice"
                checked={backlinkChoice === "calendar-only"}
                onChange={() => setBacklinkChoice("calendar-only")}
                className="mt-1"
              />
              <span>Add to calendar only</span>
            </label>
          </div>
        }
        confirmText="Continue"
        cancelText="Cancel"
        onConfirm={handleConfirmBacklinkModal}
        onCancel={() => {
          setShowBacklinkModal(false);
          setPendingCalendar(null);
        }}
      />
    </>
  );
};

export default AddToCalendarComponent;
